/**
 * @file   src\components\ProductSignUpVerificationModal.tsx
 * @brief  OTP verification modal for ProductSignUp / share-code subscribe flow.
 * @date   JUL, 2026
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import { React, useState, useEffect, useNavigate, Link, useParams, Button, Offcanvas, Modal } from '../utils/thirdpartyComponents';
import Verifyicon from "../assets/images/verify-img.svg";
import CWInput from "src/components/CWInput";
import "../components/OffCanvas.scss";
import { pageURLs } from 'src/utils/pageUrls';
import { CommonMessages } from 'src/messages/validationMessages';
import axios from '../config/axiosConfig';
import { MessageToaster } from "src/utils/toastUtil";
import { SignupApi, LocationApi, AuthApi } from "../utils/apiUrls";
import { VerifySignupRequest, locationUpdateRequest } from '../interfaces/generalInterface';
import { formatPhoneNumber, formatPhoneNumberforapipass, setLocalStorage, getMobileOperatingSystem, syncFreeSingleWashByLocation, navigateToSharePaymentIfPending, resolveProductSignupLocations, mapFetchedLocationToDisplay } from 'src/utils/helper';
import { IAuthRequest } from 'src/interfaces/authInterface';
import { useAppDispatch } from 'src/hooks';
import { validateUser } from 'src/store/actions/authAction';
import { buildSigninSchema } from 'src/validations/authSchema';
import { getActiveCountryCode } from 'src/utils/countryHelper';
import * as Yup from 'yup';
import { PHONE_MAX_LENGTH } from 'src/utils/constants';
import { getFirebaseToken} from "src/utils/FirebaseConfig";
import SmallLoader from "src/components/SmallLoader";

const ProductSignUpVerificationModal = (props: any) => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const { show, onModalHide, phoneNumber } = props;
  const toaster = new MessageToaster();
  const navigate = useNavigate();

  const [otp, setOtp] = useState(["", "", "", ""]);
  const [timer, setTimer] = useState<number>(30);
  const [showResend, setShowResend] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatedNumber, setUpdatedNumber] = useState({ custphno: '' });
  const [userphoneNo, setUserPhoneNo] = useState('');
  const [newNumberError, setNewNumberError] = useState({ custphno: '' });
  const [showEditmodal, setShowEditmodal] = useState(false);

  useEffect(() => {
    setUserPhoneNo(phoneNumber);
  }, [phoneNumber]);

  const firstInput = document.getElementById(`product-otpinput1`);
  useEffect(() => {
    if (firstInput) {
      (firstInput as HTMLInputElement).focus();
    }
  }, [firstInput]);

  useEffect(() => {
    if (show) {
      setTimer(30);
      setShowResend(false);
    }
  }, [show]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else {
      setShowResend(true);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timer]);

  const goToSharePaymentOrLocation = async () => {
    const carwashname = params.carwashname || '';
    if (navigateToSharePaymentIfPending(navigate, carwashname)) {
      return;
    }
    toaster.toastError("Unable to continue to payment.");
    navigate("/" + carwashname + "/" + pageURLs.home);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    setError(null);
    const value = e.target.value.replace(/\D/g, '');

    if (value.length === 1) {
      if (value) {
        setOtp(prevOtp => {
          const newOtp = [...prevOtp];
          newOtp[index] = value;
          return newOtp;
        });
        if (index < otp.length - 1) {
          const nextInput = document.getElementById(`product-otpinput${index + 2}`);
          if (nextInput) {
            (nextInput as HTMLInputElement).focus();
          }
        }
      }
    } else if (value.length === 4) {
      handleInput(e, index);
    } else {
      const oldvalue = value.toString().split('');
      const newvalue = oldvalue[oldvalue.length - 1];
      if (newvalue) {
        const newnumbervalue = newvalue.replace(/\D/g, '');
        if (index < otp.length - 1) {
          const nextInput = document.getElementById(`product-otpinput${index + 2}`);
          if (nextInput) {
            (nextInput as HTMLInputElement).focus();
          }
        }
        if (otp[index] === "") {
          setOtp(prevOtp => {
            const newOtp = [...prevOtp];
            newOtp[index] = newnumbervalue;
            return newOtp;
          });
        } else {
          const nextInput = document.getElementById(`product-otpinput${index + 3}`);
          if (nextInput) {
            (nextInput as HTMLInputElement).focus();
          }
          if (index < otp.length - 1) {
            setOtp(prevOtp => {
              const newOtp = [...prevOtp];
              newOtp[index + 1] = newnumbervalue;
              return newOtp;
            });
          }
        }
        if (index < otp.length - 1) {
          const nextInput = document.getElementById(`product-otpinput${index + 2}`);
          if (nextInput) {
            (nextInput as HTMLInputElement).focus();
          }
        }
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Enter") {
      handleSubmit();
    } else if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();
      if (otp[index] === "") {
        if (index > 0) {
          const prevInput = document.getElementById(`product-otpinput${index}`) as HTMLInputElement;
          if (prevInput) {
            prevInput.focus();
          }
        }
      } else {
        setOtp(prevOtp => {
          const newOtp = [...prevOtp];
          newOtp[index] = "";
          return newOtp;
        });
        const currentInput = document.getElementById(`product-otpinput${index}`) as HTMLInputElement;
        if (currentInput) {
          currentInput.focus();
        }
      }
    }
  };

  const handlePaste = (e: any) => {
    const pasteData = e.clipboardData.getData('text');
    if (pasteData.length === otp.length) {
      setOtp(pasteData.split(''));
      e.preventDefault();
      const lastInput = document.getElementById(`product-otpinput4`);
      if (lastInput) {
        (lastInput as HTMLInputElement).focus();
      }
    }
  };

  const handleInput = (e: any, _index: number) => {
    const pasteData = e.target.value;
    if (pasteData.length === otp.length) {
      setOtp(pasteData.split(''));
      e.preventDefault();
      const lastInput = document.getElementById(`product-otpinput4`);
      if (lastInput) {
        (lastInput as HTMLInputElement).focus();
      }
    }
  };

  const handleSubmit = async () => {
    const enteredOtp = otp.join("");

    if (otp.includes("")) {
      setError(CommonMessages.otp_required);
      return;
    }

    try {
      setLoading(true);
      const requestData: VerifySignupRequest = {
        phoneNumber: formatPhoneNumberforapipass(userphoneNo),
        otp: parseInt(enteredOtp),
        carWashId: localStorage.getItem('carWashId') ? parseInt(localStorage.getItem('carWashId') as string) : 0,
      };
      const { data } = await axios.post(SignupApi.VARIFY, requestData);
      if (data.error === 0) {
        setLocalStorage('CARWASH_DATA', JSON.stringify(data.data));
        const managerUserTypeId = Number(data?.data?.managerUserTypeId || 0);
        const isFreeSingleWashUser = managerUserTypeId === 3 || managerUserTypeId == 4;

        if (isFreeSingleWashUser) {
          localStorage.setItem('freeSinglewash', 'true');
        } else {
          localStorage.removeItem('freeSinglewash');
        }

        if ("Notification" in window && Notification?.permission === "granted") {
          handleGetFirebaseToken();
        } else {
          localStorage.removeItem('fcmToken');
        }

        const locationData = await axios.get(LocationApi.LOCATION_FETCH);
        const allLocations = locationData?.data?.data || [];
        const { locations: shareLocations, isProductShareFlow } = resolveProductSignupLocations(allLocations);
        const availableLocations = isProductShareFlow
          ? shareLocations
          : allLocations.map(mapFetchedLocationToDisplay);

        if (isProductShareFlow && availableLocations.length === 0) {
          handleOnHide();
          toaster.toastError("No locations available for this subscription.");
          navigate("/" + params.carwashname + "/" + pageURLs.home);
          return;
        }

        if (availableLocations.length === 1) {
          const locationId = availableLocations[0]?.locationId;
          localStorage.setItem('selectedLocation', locationId);

          const locationUpdateData: locationUpdateRequest = { locationId };
          const locationUpdate = await axios.post(LocationApi.LOCATION_UPDATE, locationUpdateData);
          handleOnHide();

          if (locationUpdate.data.error === 0) {
            syncFreeSingleWashByLocation(Number(locationId || 0));
            await goToSharePaymentOrLocation();
          } else {
            setOtp(["", "", "", ""]);
            if (firstInput) {
              (firstInput as HTMLInputElement).focus();
            }
            toaster.toastError(locationUpdate.data.error);
          }
        } else if (availableLocations.length > 1) {
          handleOnHide();
          navigate("/" + params.carwashname + "/" + pageURLs.location_switch, {
            state: { origin: 'ProductSignUpVerificationModal', frompath: 'product-signup' },
          });
        } else {
          handleOnHide();
          toaster.toastError("No locations available for this subscription.");
          navigate("/" + params.carwashname + "/" + pageURLs.home);
        }
      } else {
        setOtp(["", "", "", ""]);
        if (firstInput) {
          (firstInput as HTMLInputElement).focus();
        }
        toaster.toastError(data.message);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  const resendClick = (phoneno: string) => {
    const authRequest: IAuthRequest = {
      carWashId: localStorage.getItem('carWashId') ? parseInt(localStorage.getItem('carWashId') as string) : 0,
      phoneNumber: formatPhoneNumberforapipass(phoneno),
      CarWashURL: params.carwashname,
    };
    setOtp(["", "", "", ""]);
    setTimer(30);
    setShowResend(false);
    dispatch(validateUser(authRequest));
  };

  const handleOnHide = () => {
    setOtp(["", "", "", ""]);
    onModalHide();
  };

  const handleCloseEditmodal = () => setShowEditmodal(false);
  const handleShowEditmodal = () => setShowEditmodal(true);

  const handleNewNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone_value = e.target.value;
    if (!/^\d*$/.test(phone_value)) {
      e.target.value = phone_value.replace(/\D/g, '');
    }
    const { name, value } = e.target;
    setNewNumberError({ ...newNumberError, custphno: "" });
    setUpdatedNumber({ ...updatedNumber, [name]: formatPhoneNumber(value) });
  };

  const updatePhoneNumber = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await buildSigninSchema(getActiveCountryCode()).validate(updatedNumber, { abortEarly: false });
      setNewNumberError({ custphno: '' });

      const newData = {
        carWashId: localStorage.getItem('carWashId') ? parseInt(localStorage.getItem('carWashId') as string) : 0,
        oldPhoneNumber: formatPhoneNumberforapipass(userphoneNo),
        newPhoneNumber: formatPhoneNumberforapipass(updatedNumber.custphno),
      };

      if (newData.oldPhoneNumber === newData.newPhoneNumber) {
        setNewNumberError({ custphno: 'Please enter a different phone number than the current one' });
        return false;
      }

      try {
        toaster.toastDismiss();
        const { data } = await axios.post(SignupApi.UPDATE_PHONE_NO, newData);
        if (data?.error === 0) {
          setUserPhoneNo(updatedNumber.custphno);
          await handleCloseEditmodal();
          setTimer(30);
        } else {
          toaster.toastError(data?.message);
        }
      } catch (error: any) {
        throw new Error(error?.response?.data?.message || error?.message);
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const newErrors: any = {};
        error.inner.forEach((err) => {
          newErrors[err.path as keyof typeof updatedNumber] = err.message;
        });
        setNewNumberError(newErrors);
      }
    }
  };

  const handleGetFirebaseToken = () => {
    getFirebaseToken().then((firebaseToken: any | undefined) => {
      if (firebaseToken) {
        const deviceType = getMobileOperatingSystem();
        localStorage.setItem('fcmToken', firebaseToken);
        updateDeviceToken(deviceType, firebaseToken);
      }
    });
  };

  const updateDeviceToken = async (deviceType: number, deviceToken: string) => {
    await axios.post(AuthApi.UPDATE_DEVICE_TOKEN, {
      DeviceToken: deviceToken,
      DeviceType: deviceType,
    });
  };

  return (
    <Offcanvas
      id="verify-offcanvas"
      show={show}
      onHide={() => handleOnHide()}
      {...props}
      placement="bottom"
    >
      <Offcanvas.Header closeButton></Offcanvas.Header>
      <Offcanvas.Body>
        <div className="mobile-icon text-center"><img src={Verifyicon} alt="Car Wash App" /></div>
        <h1>Verification</h1>
        <p>Enter the verification code we just sent to your phone number.</p>
        <p className='phone'>{formatPhoneNumber(userphoneNo)}</p>
        <div className="note d-flex">
          <p>If you entered incorrect phone number.</p>
          <Button variant="primary" className="primary-btn btn-main change-btn" onClick={handleShowEditmodal}>Change phone number</Button>
        </div>
        <div className="otp-wrap d-flex justify-content-center">
          {otp.map((value, index) => (
            <CWInput
              key={index}
              id={`product-otpinput${index + 1}`}
              autoFocus={index === 0}
              name="otpinput"
              type="text"
              inputmode="numeric"
              pattern="\d*"
              errorMessage=""
              maxLength={4}
              value={value}
              onChange={(e) => handleChange(e, index)}
              onKeyPress={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
            />
          ))}
        </div>
        <div className="control-group text-center">
          <div className="timer">{`00:${timer.toString().padStart(2, '0')}`}</div>
          {!showResend ? (
            <span className="code-link">Resend Code</span>
          ) : (
            <Link to="" title="Resend Code" onClick={() => resendClick(userphoneNo)} className='active'>Resend Code</Link>
          )}
        </div>
      </Offcanvas.Body>
      <div className="offcanvas-footer">
        {error ? <span className="error-msg">{error}</span> : null}
        <Button variant="primary" className="primary-btn btn-main" onClick={handleSubmit}>Verify</Button>
      </div>
      <Modal
        show={showEditmodal}
        onHide={handleCloseEditmodal}
        centered
        className='notification-modal'>
        <form onSubmit={(e) => { updatePhoneNumber(e); }}>
          <Modal.Body>
            <CWInput
              id="product-custphno"
              name="custphno"
              type="text"
              inputmode="numeric"
              label="Change Phone Number"
              value={updatedNumber.custphno}
              onChange={handleNewNumber}
              errorMessage={newNumberError.custphno}
              maxLength={PHONE_MAX_LENGTH}
            />
          </Modal.Body>
          <Modal.Footer className='d-grid'>
            <Button variant="light" className='m-0' onClick={handleCloseEditmodal}>Cancel</Button>
            <Button variant="primary" className="primary-btn btn-main m-0" type='submit'>Save</Button>
          </Modal.Footer>
        </form>
      </Modal>
      {loading ? <SmallLoader/> : ''}
    </Offcanvas>
  );
};

export default ProductSignUpVerificationModal;
